/* ============================================================
   QuotePage — main page: upload → configure → instant quote
   ============================================================ */

import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client';
import { FileUpload } from './FileUpload';
import { MaterialSelector } from './MaterialSelector';
import { QualitySelector } from './QualitySelector';
import { QuoteDisplay } from './QuoteDisplay';
import { PerformerList } from './PerformerList';
import {
  MAKERWORLD_LINK_INITIAL,
  MakerWorldPrefillCard,
  type MakerWorldLinkState,
  type SlicerOverrideValues,
} from './MakerWorldPrefillCard';
import { QUALITY_PRESETS, type FileFormat, type Material, type ModelFileResponse, type QualityPreset, type QuoteResponse } from '../types';
import {
  POST_TOGGLES_INITIAL,
  togglePostProcessing,
  type PostTogglesState,
} from './postProcessingView';

type Phase = 'upload' | 'configure' | 'quoted';

/**
 * TASK-196: the API serializes enums as numbers (format: 0), while the TS type
 * says string — map both spellings to a safe label instead of calling
 * .toUpperCase() on whatever arrived.
 */
const FORMAT_LABELS: Record<string, string> = {
  stl: 'STL', '0': 'STL',
  obj: 'OBJ', '1': 'OBJ',
  threemf: '3MF', '2': '3MF',
  step: 'STEP', '3': 'STEP',
};

function formatLabel(format: FileFormat | number | null | undefined): string {
  if (format === null || format === undefined) return '—';
  const key = String(format).toLowerCase();
  return FORMAT_LABELS[key] ?? String(format).toUpperCase();
}

export function QuotePage({ onGoToCart }: { onGoToCart?: () => void }) {
  const [phase, setPhase] = useState<Phase>('upload');
  const [modelFile, setModelFile] = useState<ModelFileResponse | null>(null);
  const [materials, setMaterials] = useState<Material[]>([]);
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [selectedMaterial, setSelectedMaterial] = useState<string | null>(null);
  const [selectedQuality, setSelectedQuality] = useState<QualityPreset>(QUALITY_PRESETS[1]); // standard
  const [quote, setQuote] = useState<QuoteResponse | null>(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  // MW-3 (40064910): optional MakerWorld link → prefill grams/minutes.
  const [makerWorldUrl, setMakerWorldUrl] = useState('');
  const [makerWorldState, setMakerWorldState] = useState<MakerWorldLinkState>(MAKERWORLD_LINK_INITIAL);
  const [slicerValues, setSlicerValues] = useState<SlicerOverrideValues>({ grams: null, minutes: null });
  // 00a0da87: customer post-processing toggles — each performer prices
  // them by their own rates (re-quoted with the rest of the config).
  const [postToggles, setPostToggles] = useState<PostTogglesState>(POST_TOGGLES_INITIAL);

  // Load materials on mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const mats = await api.getMaterials();
        if (!cancelled) {
          setMaterials(mats);
          if (mats.length > 0) setSelectedMaterial(mats[0].id);
        }
      } catch {
        // Backend may not be running — fallback to static presets
        if (!cancelled) {
          setMaterials(getFallbackMaterials());
        }
      } finally {
        if (!cancelled) setMaterialsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleFileUploaded = useCallback((file: ModelFileResponse) => {
    setModelFile(file);
    setPhase('configure');
  }, []);

  // Auto-fetch quote when config changes
  useEffect(() => {
    if (phase !== 'configure' || !modelFile) return;

    const materialId = materials.length > 0 && selectedMaterial
      ? selectedMaterial
      : undefined;

    // If no backend materials, compute client-side quote
    if (materials.length === 0) {
      const fallback = computeClientSideQuote(modelFile, selectedQuality);
      setQuote(fallback);
      setPhase('quoted');
      return;
    }

    let cancelled = false;
    setQuoteLoading(true);
    setQuoteError(null);

    const timer = setTimeout(async () => {
      try {
        const result = await api.createQuote({
          modelFileId: modelFile.id,
          materialId,
          layerHeightMm: selectedQuality.layerHeightMm,
          infillPercent: selectedQuality.infillPercent,
          // MW-3: prefilled/edited overrides from the MakerWorld link (spec §5.3).
          slicerGrams: slicerValues.grams ?? undefined,
          slicerMinutes: slicerValues.minutes ?? undefined,
          // 00a0da87: toggles ride the quote; performers price them in matches.
          includePaint: postToggles.includePaint,
          includeSupportRemoval: postToggles.includeSupportRemoval,
        });
        if (!cancelled) {
          setQuote(result);
          setPhase('quoted');
        }
      } catch (err) {
        if (!cancelled) {
          setQuoteError(err instanceof Error ? err.message : 'Не удалось создать расчёт');
        }
      } finally {
        if (!cancelled) setQuoteLoading(false);
      }
    }, 500); // debounce

    return () => { cancelled = true; clearTimeout(timer); };
  }, [phase, modelFile, selectedMaterial, selectedQuality, materials, slicerValues, postToggles]);

  const handleReset = () => {
    setPhase('upload');
    setModelFile(null);
    setQuote(null);
    setQuoteError(null);
  };

  // Real backend quotes get performer matches; the offline client-side
  // fallback quote does not.
  const hasPerformerMatches =
    quote !== null && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(quote.quoteId);

  return (
    <div className="container" style={{ paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-2xl)' }}>
      {/* Hero heading */}
      <div style={{ textAlign: 'center', marginBottom: 'var(--space-xl)' }}>
        <h1
          style={{
            fontSize: 'var(--fs-display)',
            fontWeight: 'var(--fw-bold)',
            letterSpacing: '-0.02em',
            marginBottom: 'var(--space-sm)',
          }}
        >
          Расчёт 3D-печати
        </h1>
        <p
          style={{
            fontSize: 'var(--fs-body)',
            color: 'var(--color-text-secondary)',
            maxWidth: '480px',
            margin: '0 auto',
          }}
        >
          Загрузите модель — мы рассчитаем стоимость мгновенно. Без регистрации.
        </p>
      </div>

      <div
        style={{
          display: 'grid',
          gap: 'var(--space-lg)',
          maxWidth: '640px',
          margin: '0 auto',
        }}
      >
        {/* Step 1: Upload */}
        {phase === 'upload' && <FileUpload onUploaded={handleFileUploaded} />}

        {/* MW-3 (40064910): optional MakerWorld link prefill — spec §5,
            lives alongside the upload (never replaces it) and stays
            editable through the configure phase. */}
        <MakerWorldPrefillCard
          url={makerWorldUrl}
          onUrlChange={setMakerWorldUrl}
          state={makerWorldState}
          onStateChange={setMakerWorldState}
          values={slicerValues}
          onValuesChange={setSlicerValues}
          materials={materials}
          onMaterialSuggested={setSelectedMaterial}
        />

        {/* Step 2: Configure + Quote */}
        {phase !== 'upload' && modelFile && (
          <>
            {/* Uploaded file bar */}
            <div
              className="card"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: 'var(--space-md) var(--space-lg)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-sm)' }}>
                <FileIcon />
                <div>
                  <p style={{ fontSize: 'var(--fs-body)', fontWeight: 'var(--fw-medium)' }}>
                    {modelFile.originalFileName}
                  </p>
                  <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)' }}>
                    {(Number(modelFile.sizeBytes ?? 0) / 1024 / 1024).toFixed(1)} МБ · {formatLabel(modelFile.format)}
                  </p>
                </div>
              </div>
              <button
                className="btn btn-tertiary"
                onClick={handleReset}
                style={{ fontSize: 'var(--fs-caption)', height: 'auto', padding: '8px 12px' }}
              >
                Заменить
              </button>
            </div>

            {/* Material + Quality selectors */}
            <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-lg)' }}>
              <MaterialSelector
                materials={materials}
                selected={selectedMaterial}
                onSelect={setSelectedMaterial}
                loading={materialsLoading}
              />
              <QualitySelector
                selected={selectedQuality.key}
                onSelect={setSelectedQuality}
              />
              {/* 00a0da87: post-processing toggles — priced per performer; */}
              {/* unknown paint area / support mass → «уточняется в чате». */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-xs)' }}>
                <p style={{ fontSize: 'var(--fs-caption)', color: 'var(--color-text-secondary)', margin: 0 }}>
                  Постобработка — цену называет каждый исполнитель
                </p>
                <div style={{ display: 'flex', gap: 'var(--space-xs)', flexWrap: 'wrap' }}>
                  <PostToggleChip
                    active={postToggles.includePaint}
                    label="Покраска"
                    onClick={() => setPostToggles((t) => togglePostProcessing(t, 'includePaint'))}
                  />
                  <PostToggleChip
                    active={postToggles.includeSupportRemoval}
                    label="Удаление поддержек"
                    onClick={() => setPostToggles((t) => togglePostProcessing(t, 'includeSupportRemoval'))}
                  />
                </div>
              </div>
            </div>

            {/* Quote result */}
            {quoteLoading && (
              <div className="card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 'var(--space-sm)', padding: 'var(--space-xl)' }}>
                <div className="spinner" />
                <span style={{ fontSize: 'var(--fs-body)', color: 'var(--color-text-secondary)' }}>
                  Считаем стоимость...
                </span>
              </div>
            )}

            {quoteError && (
              <div className="card" style={{ padding: 'var(--space-md) var(--space-lg)' }}>
                <p style={{ fontSize: 'var(--fs-body)', color: 'var(--color-danger)' }}>{quoteError}</p>
              </div>
            )}

            {!quoteLoading && quote && <QuoteDisplay quote={quote} modelFile={modelFile} />}

            {/* TASK-197 + 3830fe0d: matching performers with prices; the
                pick adds a quote-print line to the cart — ordering itself
                happens at cart checkout, never here. */}
            {!quoteLoading && quote && hasPerformerMatches && (
              <PerformerList quote={quote} onGoToCart={onGoToCart} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* 00a0da87: light-theme toggle chip — accent border when on, no gradients
   or heavy shadows (DESIGN-SYSTEM.md). Same tactility as G8-D1 chips. */
function PostToggleChip({ active, label, onClick }: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-xxs)',
        padding: 'var(--space-xs) var(--space-md)',
        borderRadius: 'var(--radius-md)',
        border: active
          ? '2px solid var(--color-primary)'
          : '1px solid var(--color-separator)',
        background: 'var(--color-surface)',
        cursor: 'pointer',
        color: active ? 'var(--color-primary)' : 'var(--color-text-primary)',
        fontWeight: active ? 'var(--fw-semibold)' : 'var(--fw-medium)',
        fontSize: 'var(--fs-body)',
      }}
    >
      {active && <span aria-hidden>✓</span>}
      <span>{label}</span>
    </button>
  );
}

/* ── Fallback materials (when backend is offline) ─────────── */

function getFallbackMaterials(): Material[] {
  return [
    { id: 'pla', name: 'PLA', type: 'Pla', pricePerGramRub: 0.80, color: 'Любой', densityGperCm3: 1.24, isActive: true },
    { id: 'petg', name: 'PETG', type: 'Petg', pricePerGramRub: 0.90, color: 'Любой', densityGperCm3: 1.27, isActive: true },
    { id: 'abs', name: 'ABS', type: 'Abs', pricePerGramRub: 0.75, color: 'Любой', densityGperCm3: 1.04, isActive: true },
    { id: 'tpu', name: 'TPU 95A', type: 'Tpu', pricePerGramRub: 1.50, color: 'Любой', densityGperCm3: 1.21, isActive: true },
  ];
}

/* ── Client-side quote (when backend is offline) ──────────── */

function computeClientSideQuote(file: ModelFileResponse, quality: QualityPreset): QuoteResponse {
  const densityMap: Record<string, number> = {
    pla: 1.24, petg: 1.27, abs: 1.04, tpu: 1.21,
  };
  const priceMap: Record<string, number> = {
    pla: 0.80, petg: 0.90, abs: 0.75, tpu: 1.50,
  };

  const density = densityMap.pla ?? 1.24;
  const pricePerGram = priceMap.pla ?? 0.80;

  const volumeCm3 = file.volumeMm3 / 1000;
  const solidGrams = volumeCm3 * density;
  const infillFactor = quality.infillPercent / 100 * 0.6 + 0.4;
  const estimatedGrams = solidGrams * infillFactor;
  const printMinutes = Math.ceil(volumeCm3 * 6 * infillFactor);

  const materialCost = Math.round(estimatedGrams * pricePerGram);
  const machineCost = Math.round((printMinutes / 60) * 300);
  const setupFee = 100;
  const postProcessing = 0;
  const subtotal = materialCost + machineCost + setupFee + postProcessing;
  const risk = Math.round(subtotal * 0.05);
  const platformFee = Math.round((subtotal + risk) * 0.15);
  const total = subtotal + risk + platformFee;

  return {
    quoteId: 'client-side',
    setupFee,
    materialCost,
    machineCost,
    postProcessingCost: postProcessing,
    platformFee,
    totalPrice: total,
    estimatedPrintMinutes: printMinutes,
    estimatedMaterialGrams: Math.round(estimatedGrams * 10) / 10,
  };
}

/* ── File icon ────────────────────────────────────────────── */

function FileIcon() {
  return (
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
      <path
        d="M8 4H18L24 10V26C24 27.1 23.1 28 22 28H8C6.9 28 6 27.1 6 26V6C6 4.9 6.9 4 8 4Z"
        stroke="var(--color-primary)"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M18 4V10H24" stroke="var(--color-primary)" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}
